import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  Alert,
  Anchor,
  Button,
  Card,
  Fieldset,
  Group,
  Loader,
  NativeSelect,
  Radio,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { FORMAT_RULES, type Format } from '@quizee/core';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, api, errorMessage } from '../api/client';
import type { BrandSummary } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { ErrorNote, ProblemList } from '../components/ErrorNote';
import { Loading } from '../components/Loading';
import { FORMAT_CHOICES, promptExample } from '../format';

/**
 * Asking a model for a first draft.
 *
 * Three questions: what kind of thing this is, which article it should come from, and what it
 * should be about. The format is asked first because it decides what a useful answer to the third
 * looks like, which is why the example in the box changes with it.
 *
 * The last two are one question asked two ways and either will do. A link is what an editor
 * actually has in front of them when the thought occurs, and a brief is what they have when the
 * piece is not written yet. Both together is a real request rather than a conflict: the article is
 * the material and the brief is the steer.
 *
 * Which links may be read is the API's decision and is not repeated here. Drafting only ever
 * fetches the brand's own domains, which is the whole reason it is allowed to fetch anything at
 * all (D38), so the rule lives in one place and this screen says what the rule is and shows the
 * refusal when a link breaks it.
 *
 * What comes back is an ordinary interaction, saved as a draft with a provenance flag on it, so
 * this screen hands straight over to the normal editor. Nothing about a drafted interaction is
 * edited anywhere else.
 */

type Stage = 'asking' | 'waiting';

/** A refusal we have words for, as opposed to something going wrong. */
type Refusal =
  | { readonly kind: 'url_not_allowed'; readonly message: string }
  | { readonly kind: 'article_unreachable'; readonly message: string; readonly reason: string }
  | { readonly kind: 'draft_failed'; readonly message: string }
  | { readonly kind: 'rate_limited'; readonly message: string }
  | { readonly kind: 'failed'; readonly message: string };

export function DraftInteraction() {
  const { org } = useAuth();
  const orgId = org?.org_id ?? null;
  const navigate = useNavigate();

  const [format, setFormat] = useState<Format>('poll');
  const [prompt, setPrompt] = useState('');
  const [articleUrl, setArticleUrl] = useState('');
  const [brands, setBrands] = useState<readonly BrandSummary[] | null>(null);
  const [brandId, setBrandId] = useState('');
  const [brandProblem, setBrandProblem] = useState<string | null>(null);

  const [stage, setStage] = useState<Stage>('asking');
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  const loadBrands = useCallback(() => {
    if (orgId === null) return;
    setBrandProblem(null);
    setBrands(null);
    api
      .brands(orgId)
      .then((body) => {
        setBrands(body.brands);
        setBrandId((current) => (current !== '' ? current : (body.brands[0]?.brand_id ?? '')));
      })
      .catch((error: unknown) => setBrandProblem(errorMessage(error)));
  }, [orgId]);

  useEffect(loadBrands, [loadBrands]);

  const name = FORMAT_RULES[format].label.toLowerCase();
  const brand = brands?.find((candidate) => candidate.brand_id === brandId) ?? null;
  const domains = brand?.domains ?? [];

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (orgId === null || stage === 'waiting') return;

    // Nothing is asked of the model until there is something to ask about, and a link and a brief
    // are two ways of saying the same thing, so either one is enough.
    const description = prompt.trim();
    const url = articleUrl.trim();
    const found: string[] = [];
    if (description === '' && url === '') {
      found.push(`Paste a link to an article, say what this ${name} should be about, or both.`);
    }
    if (url !== '' && !looksLikeWebAddress(url)) {
      found.push(
        'That is not a web address we can read. Copy the article link from your browser, starting with https.',
      );
    }
    if (brandId === '') found.push(`Choose which brand this ${name} belongs to.`);
    if (found.length > 0) {
      setProblems(found);
      return;
    }

    setProblems([]);
    setRefusal(null);
    setStage('waiting');
    try {
      const body = await api.draftInteraction(orgId, {
        format,
        brandId,
        ...(description === '' ? {} : { prompt: description }),
        ...(url === '' ? {} : { articleUrl: url }),
      });
      void navigate(`/interactions/${body.interaction.interaction_id}`, { replace: true });
    } catch (error) {
      setStage('asking');
      setRefusal(asRefusal(error));
    }
  }

  return (
    <>
      <Text size="sm" mb="sm">
        <Anchor component={Link} to="/interactions/new" size="sm">
          Back to formats
        </Anchor>
      </Text>
      <Title order={1} size="h2">
        Ask for a first draft
      </Title>
      <Text size="lg" mt="sm" mb="lg">
        Point it at one of your articles, say what you want, or do both, and a model writes a first
        draft. You read it, change anything you like, and approve it. Nothing reaches a reader until
        you do.
      </Text>

      {brandProblem !== null && <ErrorNote message={brandProblem} onRetry={loadBrands} />}
      {brandProblem === null && brands === null && <Loading label="Loading your brands" />}
      {brands !== null && brands.length === 0 && (
        <ErrorNote message="This publisher has no brands yet, so there is nowhere to put a draft. Ask an owner to add one." />
      )}

      {refusal !== null && (
        <RefusalNote refusal={refusal} format={format} brand={brand} domains={domains} />
      )}

      {stage === 'waiting' ? (
        <Waiting name={name} fromArticle={articleUrl.trim() !== ''} />
      ) : (
        <form onSubmit={(event) => void submit(event)}>
          <ProblemList problems={problems} title="Before a draft can be written:" />

          <Fieldset variant="unstyled">
            <Stack gap="lg">
              <Radio.Group
                label="What are you making?"
                description="This is the one thing that cannot be changed afterwards."
                value={format}
                onChange={(value) => setFormat(value as Format)}
              >
                <Stack gap="xs" mt="xs">
                  {FORMAT_CHOICES.map((choice) => (
                    <Radio
                      key={choice.format}
                      value={choice.format}
                      label={choice.label}
                      description={choice.what}
                    />
                  ))}
                </Stack>
              </Radio.Group>

              {brands !== null && brands.length > 1 && (
                <NativeSelect
                  id="brand"
                  label="Brand"
                  description={`Which title this ${name} runs on.`}
                  value={brandId}
                  onChange={(event) => setBrandId(event.target.value)}
                  data={brands.map((entry) => ({ value: entry.brand_id, label: entry.name }))}
                  style={{ maxWidth: '18rem' }}
                />
              )}

              <TextInput
                id="draft-article-url"
                /*
                 * Not type="url". The browser would refuse the form with its own bubble, which
                 * says "enter a URL" and nothing about which sites drafting may read. The check
                 * below says that instead, in the same list as every other problem on this form.
                 */
                inputMode="url"
                label="Article address"
                description={articleDescription(domains)}
                placeholder={articlePlaceholder(domains)}
                value={articleUrl}
                maxLength={2048}
                onChange={(event) => setArticleUrl(event.target.value)}
                style={{ maxWidth: '34rem' }}
              />

              <Textarea
                id="draft-prompt"
                label={`What should this ${name} be about?`}
                description={
                  articleUrl.trim() === ''
                    ? 'Write it the way you would explain it to a colleague. Pasting a few lines from the article helps.'
                    : 'Optional when you have given a link. Add anything you want steered: how many questions, what to leave out, what to lead on.'
                }
                placeholder={promptExample(format)}
                autosize
                minRows={4}
                maxRows={12}
                maxLength={2000}
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
              />
            </Stack>
          </Fieldset>

          <Group mt="lg" gap="md" align="center">
            <Button type="submit" disabled={brands === null}>
              {refusal === null ? 'Write the draft' : 'Try again'}
            </Button>
            <Anchor component={Link} to={`/interactions/new/${format}`} size="sm">
              Write it yourself instead
            </Anchor>
          </Group>
        </form>
      )}
    </>
  );
}

/**
 * What the address box says it will take.
 *
 * The domains are named rather than described, because "your own site" is not something an editor
 * can check a link against and `herald.co.uk` is. A brand with no website yet says so: pasting a
 * link there could never work, and finding that out from a refusal would be worse.
 */
function articleDescription(domains: readonly string[]): string {
  if (domains.length === 0) {
    return 'Drafting only reads your own pages, and this brand has no website on it yet, so there is nothing it can read. Describe what you want instead.';
  }
  const list = domains.join(', ');
  return `Optional. A link to one of your own articles, which is read and drafted from. Only ${list} can be read: drafting never reads anybody else's site.`;
}

function articlePlaceholder(domains: readonly string[]): string {
  const first = domains[0];
  return first === undefined
    ? 'https://your-site.co.uk/news/the-article'
    : `https://${first}/news/the-article`;
}

/**
 * Whether this is worth sending at all.
 *
 * Deliberately only a scheme and a host: whether a host is one of the brand's own is decided by
 * the API against the domains the publisher proved they control, and a second copy of that rule in
 * the browser could only ever drift from the one that actually protects anything. This catches the
 * typo, not the policy.
 */
function looksLikeWebAddress(value: string): boolean {
  try {
    const parsed = new URL(value);
    return (
      (parsed.protocol === 'http:' || parsed.protocol === 'https:') && parsed.hostname.includes('.')
    );
  } catch {
    return false;
  }
}

/**
 * The wait.
 *
 * Drafting takes several seconds, longer when a page has to be fetched first, which is long enough
 * for somebody to wonder whether they have broken something. So this says what is happening rather
 * than spinning at them. It is a live region, so a screen reader hears it too.
 */
function Waiting({ name, fromArticle }: { name: string; fromArticle: boolean }) {
  return (
    <Card component="section" withBorder radius="lg" p="lg" role="status" aria-live="polite">
      <Group gap="sm" align="center">
        <Loader size="sm" type="dots" />
        <Text fw={600}>Writing your first draft</Text>
      </Group>
      <Text mt="xs">
        This usually takes a few seconds.{' '}
        {fromArticle
          ? `We are reading your article and turning it into a ${name} with questions and answers.`
          : `A model is reading what you asked for and turning it into a ${name} with questions and answers.`}
      </Text>
      <Text c="dimmed" size="sm" mt="xs">
        It opens here as a draft that only your team can see. No reader sees any of it until you
        have read it, approved it and published it.
      </Text>
    </Card>
  );
}

/**
 * A draft that did not happen.
 *
 * All of these are ordinary outcomes rather than failures of the product, so each one says what
 * happened and leaves two ways forward on the screen: ask again, which the form below still allows
 * with everything typed still in it, or go and write the thing by hand.
 */
function RefusalNote({
  refusal,
  format,
  brand,
  domains,
}: {
  refusal: Refusal;
  format: Format;
  brand: { brand_id: string; name: string } | null;
  domains: readonly string[];
}) {
  const name = FORMAT_RULES[format].label.toLowerCase();

  return (
    <Alert
      role="alert"
      color={refusal.kind === 'failed' ? 'red' : 'yellow'}
      variant="light"
      radius="md"
      my="md"
      title={heading(refusal)}
    >
      <Text size="sm">{refusal.message}</Text>
      <Text size="sm" mt="xs">
        {nextStep(refusal, name)}
      </Text>
      {refusal.kind === 'url_not_allowed' && brand !== null && (
        <Text size="sm" mt="xs">
          {domains.length === 0
            ? `${brand.name} has no website on it yet, so there is nothing drafting is allowed to read.`
            : `Drafting only ever reads ${brand.name}'s own pages, which today means ${domains.join(', ')}.`}{' '}
          <Anchor component={Link} to={`/settings/brands/${brand.brand_id}`} size="sm">
            See this brand&apos;s websites
          </Anchor>
        </Text>
      )}
      <Text size="sm" mt="xs">
        <Anchor component={Link} to={`/interactions/new/${format}`} size="sm">
          Write the {name} yourself instead
        </Anchor>
      </Text>
    </Alert>
  );
}

function heading(refusal: Refusal): string {
  switch (refusal.kind) {
    case 'url_not_allowed':
      return "That link is not on this brand's own site";
    case 'article_unreachable':
      return 'We could not read that page';
    case 'draft_failed':
      return 'The model could not write that';
    case 'rate_limited':
      return 'Too many drafts in a row';
    default:
      return 'That did not work';
  }
}

function nextStep(refusal: Refusal, name: string): string {
  switch (refusal.kind) {
    case 'url_not_allowed':
      return (
        'Paste a link from a site this brand publishes on, or leave the address out and describe the ' +
        name +
        ' instead. Everything you typed is still below.'
      );
    case 'article_unreachable':
      return unreachableStep(refusal.reason);
    case 'draft_failed':
      return `Try describing the ${name} differently, with a bit more about what readers should be asked. Your description is still below.`;
    case 'rate_limited':
      return 'Wait a minute or two and ask again. Everything you typed is still below, so nothing is lost.';
    default:
      return 'Ask again in a moment. Everything you typed is still below.';
  }
}

/**
 * What to do about a page we could not read, per reason.
 *
 * The API answers with a word from a closed set rather than a message to parse, because "the site
 * did not answer" and "that is a PDF" are different problems with different answers, and an editor
 * who cannot tell them apart will retry the one that will never work.
 */
function unreachableStep(reason: string): string {
  switch (reason) {
    case 'not_html':
      return "That address is not a web page we can read, such as a PDF or a picture. Use the article's own page on your site.";
    case 'too_large':
      return 'That page is bigger than we will read. Try a shorter piece, or describe what you want instead.';
    case 'timeout':
      return 'Your site did not answer in time. Check the page opens in your browser, then ask again.';
    case 'blocked':
      return 'That address does not point at the public internet, so we will not fetch it. Use the address a reader would use.';
    case 'bad_url':
      return 'Copy the address from your browser, starting with https, with nothing after the page itself.';
    default:
      return 'Check the page opens in your browser, then ask again. You can also leave the address out and describe what you want.';
  }
}

/**
 * Which answer this is.
 *
 * The error code is what decides, and the status is only a fallback, because two of these share a
 * status: a draft the model could not write and a page we could not read are both 422 and need
 * completely different sentences.
 */
function asRefusal(error: unknown): Refusal {
  const message = errorMessage(error);
  if (error instanceof ApiError) {
    if (error.code === 'url_not_allowed') return { kind: 'url_not_allowed', message };
    if (error.code === 'article_unreachable') {
      return { kind: 'article_unreachable', message, reason: error.reason ?? '' };
    }
    if (error.code === 'draft_failed') return { kind: 'draft_failed', message };
    if (error.code === 'rate_limited' || error.status === 429) {
      return { kind: 'rate_limited', message };
    }
    // Only reached by a proxy that rewrote the body: a 422 with no code we know is far more likely
    // to be the model than the fetch, since a fetch failure is the newer of the two.
    if (error.status === 422) return { kind: 'draft_failed', message };
  }
  return { kind: 'failed', message };
}
